import React, { useState, useEffect } from 'react';
import NewHeader from './components/NewHeader';
import ProgramSchedule from './components/ProgramSchedule';
import UpdatedStatistics from './components/UpdatedStatistics';
// import UpdatedNewsSection from './components/UpdatedNewsSection';
import NewFooter from './components/NewFooter';
import Player from './components/Player';
// import Playlist from './components/Playlist';
import MusicLibrary from './components/MusicLibrary';
import Dashboard from './components/Dashboard';
import { audioData, liveStream } from './data/audioData';
import CalligraphySVG from './assest/Calligraphy/Calligraphy1.svg';
import CalligraphySVGBlue from './assest/Calligraphy/download.svg';

import Calligraphy2SVG from './assest/Calligraphy/Calligraphy2.svg';

/**
 * Main App Component
 * Manages global state for audio playback, favorites, and current track
 */
function App() {
  // State management
  const [currentTrack, setCurrentTrack] = useState(liveStream);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true); // Minimal modal for browser autoplay requirement
  const [favorites, setFavorites] = useState([]);
  // const [queue] = useState(audioData);
  const [listeningStats, setListeningStats] = useState({
    totalListeningTime: 0,
    tracksPlayed: 0,
    favoriteCount: 0,
    weeklyListening: 0,
    todayListening: 0
  });
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  
  // Auto-play playlist states
  // const [isAutoPlayMode, setIsAutoPlayMode] = useState(false);
  const [playbackMode, setPlaybackMode] = useState('repeat'); // 'repeat', 'repeat-one', 'normal'
  const [isShuffled, setIsShuffled] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  // Check URL hash for dashboard
  useEffect(() => {
    const handleHashChange = () => {
      setShowDashboard(window.location.hash === '#dashboard');
    };
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Set page direction to RTL
  useEffect(() => {
    document.documentElement.dir = 'rtl';
    document.body.dir = 'rtl';
  }, []);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('radioFavorites');
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (error) {
        console.error('Error loading favorites:', error);
      }
    }

    // Load listening stats
    const savedStats = localStorage.getItem('radioStats');
    if (savedStats) {
      try {
        setListeningStats(JSON.parse(savedStats));
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    }

    // Load recently played
    const savedRecent = localStorage.getItem('radioRecentlyPlayed');
    if (savedRecent) {
      try {
        setRecentlyPlayed(JSON.parse(savedRecent));
      } catch (error) {
        console.error('Error loading recently played:', error);
      }
    }
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('radioFavorites', JSON.stringify(favorites));
    setListeningStats(prev => ({
      ...prev,
      favoriteCount: favorites.length
    }));
  }, [favorites]);

  // Save stats to localStorage
  useEffect(() => {
    localStorage.setItem('radioStats', JSON.stringify(listeningStats));
  }, [listeningStats]);

  // Save recently played to localStorage
  useEffect(() => {
    localStorage.setItem('radioRecentlyPlayed', JSON.stringify(recentlyPlayed));
  }, [recentlyPlayed]);

  // Track listening time
  useEffect(() => {
    let interval;
    if (isPlaying && currentTrack) {
      if (!sessionStartTime) {
        setSessionStartTime(Date.now());
      }
      
      interval = setInterval(() => {
        setListeningStats(prev => ({
          ...prev,
          totalListeningTime: prev.totalListeningTime + 1,
          weeklyListening: prev.weeklyListening + 1,
          todayListening: prev.todayListening + 1
        }));
      }, 1000);
    } else {
      setSessionStartTime(null);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, currentTrack, sessionStartTime]);

  // /**
  //  * Handle track selection from playlist
  //  * Automatically starts playing the selected track
  //  */
  // const handleSelectTrack = (track) => {
  //   if (currentTrack?.id === track.id && isPlaying) {
  //     // If same track is playing, pause it
  //     setIsPlaying(false);
  //   } else {
  //     // Exit auto playlist mode when manually selecting a track
  //     if (isAutoPlayMode) {
  //       setIsAutoPlayMode(false);
  //     }
  //     
  //     // Load and play new track
  //     setCurrentTrack(track);
  //     setIsPlaying(true);
  //     
  //     // Add to recently played
  //     const newRecentItem = {
  //       track,
  //       playedAt: new Date().toISOString()
  //     };
  //     setRecentlyPlayed(prev => {
  //       const filtered = prev.filter(item => item.track.id !== track.id);
  //       return [newRecentItem, ...filtered].slice(0, 10);
  //     });
  //     
  //     // Update tracks played count
  //     setListeningStats(prev => ({
  //       ...prev,
  //       tracksPlayed: prev.tracksPlayed + 1
  //     }));
  //   }
  // };

  /**
   * Toggle play/pause state
   */
  const handlePlayPause = (playing) => {
    console.log('handlePlayPause called with:', playing);
    console.log('Current track:', currentTrack);
    setIsPlaying(playing);
  };




  /**
   * Toggle favorite status for a track
   */
  const handleToggleFavorite = (track) => {
    setFavorites(prev => {
      const isFavorite = prev.some(fav => fav.id === track.id);
      if (isFavorite) {
        return prev.filter(fav => fav.id !== track.id);
      } else {
        return [...prev, track];
      }
    });
  };

  /**
   * Play next track
   */
  const handleNext = () => {
    const currentIndex = audioData.findIndex(track => track.id === currentTrack?.id);
    if (currentIndex < audioData.length - 1) {
      const nextTrack = audioData[currentIndex + 1];
      setCurrentTrack(nextTrack);
      setIsPlaying(true);
    } else if (playbackMode === 'repeat') {
      setCurrentTrack(audioData[0]);
      setIsPlaying(true);
    }
  };

  /**
   * Play previous track
   */
  const handlePrevious = () => {
    const currentIndex = audioData.findIndex(track => track.id === currentTrack?.id);
    if (currentIndex > 0) {
      const prevTrack = audioData[currentIndex - 1];
      setCurrentTrack(prevTrack);
      setIsPlaying(true);
    } else if (playbackMode === 'repeat') {
      setCurrentTrack(audioData[audioData.length - 1]);
      setIsPlaying(true);
    }
  };

  /**
   * Toggle playback mode (repeat, repeat-one, normal)
   */
  const handleTogglePlaybackMode = () => {
    setPlaybackMode(prev => {
      if (prev === 'normal') return 'repeat';
      if (prev === 'repeat') return 'repeat-one';
      return 'normal';
    });
  };

  /**
   * Toggle shuffle mode
   */
  const handleToggleShuffle = () => {
    setIsShuffled(prev => !prev);
  };

  /**
   * Handle track end - auto play next
   */
  const handleTrackEnd = () => {
    if (playbackMode === 'repeat-one') {
      setIsPlaying(true);
    } else {
      handleNext();
    }
  };

  // Show Dashboard if active
  if (showDashboard) {
    return (
      <Dashboard onBack={() => {
        window.location.hash = '';
        setShowDashboard(false);
      }} />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-dark via-primary to-gray-900">
      {/* Header Navigation */}
      <NewHeader />
      
      {/* Minimal Start Modal - Required by Browser Autoplay Policy */}
      {showWelcome && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50 animate-fade-in"
          style={{backgroundColor: 'rgba(56, 76, 101, 0.98)'}}
        >
          {/* Calligraphy Background */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden flex items-center justify-center">
            {/* Calligraphy 2 SVG - Center only */}
            <div className="w-full h-full max-w-4xl max-h-screen opacity-15 animate-float">
              <img 
                src={Calligraphy2SVG}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          
          {/* Compact Content */}
          <div className="text-center px-8 py-12 max-w-2xl relative z-10">
            {/* شعار الإذاعة */}
            <div className="mb-8 space-y-4" dir="rtl">
              <p className="text-gold text-lg md:text-xl font-semibold leading-relaxed">
                حين تكون الأرض وحدتنا
              </p>
              <p className="text-gold text-lg md:text-xl font-semibold leading-relaxed">
                ويكون الجيش درعنا الحصين
              </p>
              <p className="text-white text-xl md:text-2xl font-bold leading-relaxed mt-4">
                هنا تولدُ إذاعةُ جامعة زايد العسكرية
              </p>
              <p className="text-gold-light text-lg md:text-xl font-semibold leading-relaxed">
                صوتُ القيادة، روحُ الشباب، صرحٌ وأثير
              </p>
            </div>

            {/* Live Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-gold to-gold-light px-6 py-3 rounded-full mb-6 shadow-2xl">
              <span className="w-3 h-3 bg-white rounded-full animate-pulse"></span>
              <span className="text-white font-bold text-lg">البث المباشر</span>
            </div>
            
            {/* Title */}
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-3" dir="rtl">
              إذاعة جامعة زايد العسكرية
            </h2>
            
            <p className="text-gold text-xl md:text-2xl font-bold mb-8" dir="rtl">
              فخر ورفعة
            </p>
            
            {/* Single Large Play Button */}
            <button
              onClick={() => {
                setShowWelcome(false);
                setCurrentTrack(liveStream);
                setIsPlaying(true);
              }}
              className="w-full bg-gradient-to-r from-gold via-gold-light to-gold text-white text-2xl font-bold py-6 rounded-2xl shadow-2xl hover:scale-105 transform transition-all duration-300 flex items-center justify-center gap-3 mb-4"
              autoFocus
            >
              <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
              </svg>
              <span>ابدأ الاستماع</span>
            </button>
            
            {/* Small Skip Text */}
            <button
              onClick={() => setShowWelcome(false)}
              className="text-gold/70 hover:text-gold text-sm underline transition-colors"
            >
              تصفح بدون تشغيل
            </button>
          </div>
        </div>
      )}

      {/* Main Content Container */}
      <main>
        {/* Hero Section - البث المباشر */}
        {/* <HeroSection
          currentProgram={{
            type: 'program',
            title: currentTrack?.titleAr || currentTrack?.title || 'برنامج الصباح الإذاعي',
            presenter: currentTrack?.artist || 'أحمد محمد',
            image: currentTrack?.cover,
            startTime: '10:00',
            endTime: '12:00',
            description: currentTrack?.description || 'برنامج صباحي متنوع يناقش آخر الأخبار والأحداث مع مجموعة من الضيوف المميزين'
          }}
          isPlaying={isPlaying}
          onPlayPause={handlePlayPause}
        /> */}

        {/* Programs Timeline - الخط الزمني للبرامج */}
        {/* <ProgramsTimeline /> */}
        <section 
          className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
          id="live"
        > 
          {/* خلفية متدرجة عسكرية قوية */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-dark to-beige-dark">
            {/* طبقة تدرج إضافية */}
            <div className="absolute inset-0 bg-gradient-to-tr from-gold/20 via-transparent to-primary/30"></div>
            
            {/* نمط خطوط قطرية عسكرية */}
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 50px, rgba(172, 130, 58, 0.5) 50px, rgba(172, 130, 58, 0.5) 100px)',
            }}></div>
            
            {/* أشكال هندسية عسكرية متحركة */}
            <div className="absolute top-20 right-10 w-64 h-64 border-4 border-gold/20 transform rotate-45 animate-pulse"></div>
            <div className="absolute bottom-32 left-20 w-80 h-80 border-4 border-beige/20 transform -rotate-12"></div>
            <div className="absolute top-1/2 right-1/3 w-48 h-48 border-4 border-gold/30 rounded-full"></div>
            
            {/* نمط النقاط الشعاعية */}
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: 'radial-gradient(circle, rgba(172, 130, 58, 0.4) 2px, transparent 2px)',
              backgroundSize: '50px 50px'
            }}></div>
            
            {/* خطوط أفقية رقيقة */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent"></div>
            <div className="absolute top-1/4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-beige/50 to-transparent"></div>
            <div className="absolute bottom-1/4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent"></div>
            
            {/* شعاع ضوئي من الزاوية */}
            
            {/* Calligraphy SVG as top layer - Bottom Right */}
            <div className="absolute -bottom-10 -right-10 w-[500px] h-[500px] opacity-40 z-10 animate-float">
              <img 
                src={CalligraphySVG}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Calligraphy SVG as top layer - Top Left */}
            <div className="absolute -top-10 -left-10 w-[500px] h-[500px] opacity-40 transform rotate-180 z-10 animate-float-reverse">
              <img 
                src={CalligraphySVG}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Content Container with Glass Effect */}
          <div className="container mx-auto px-4 py-8 relative z-10">
            {/* Decorative Military Badge - Top */}
            <div className="flex justify-center mb-6">
              <div className="bg-gradient-to-r from-gold via-gold-light to-gold px-8 py-2 rounded-full shadow-2xl border-2 border-gold-light">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                  <span className="text-white font-bold text-sm tracking-wider">البث المباشر - إذاعة جامعة زايد العسكرية</span>
                  <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Main Grid with Backdrop Blur */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              {/* Main Player - Takes 2 columns on desktop */}
              <div className="lg:col-span-2">
                <div className="relative">
                  {/* Corner Decorations */}
                  <div className="absolute -top-3 -right-3 w-8 h-8 border-t-4 border-r-4 border-gold"></div>
                  <div className="absolute -bottom-3 -left-3 w-8 h-8 border-b-4 border-l-4 border-gold"></div>
                  
                  <Player
                    currentTrack={currentTrack}
                    isPlaying={isPlaying}
                    onPlayPause={handlePlayPause}
                    favorites={favorites}
                    onToggleFavorite={handleToggleFavorite}
                    onNext={handleNext}
                    onPrevious={handlePrevious}
                    playbackMode={playbackMode}
                    onTogglePlaybackMode={handleTogglePlaybackMode}
                    onToggleShuffle={handleToggleShuffle}
                    isShuffled={isShuffled}
                    onTrackEnd={handleTrackEnd}
                  />
                </div>
              </div>

              {/* Playlist - Takes 1 column on desktop */}
              <div className="lg:col-span-1">
                <div className="relative">
                  {/* Corner Decorations */}
                  <div className="absolute -top-3 -left-3 w-8 h-8 border-t-4 border-l-4 border-gold"></div>
                  <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-4 border-r-4 border-gold"></div>
                  
                  {/* <Playlist
                    tracks={queue}
                    currentTrack={currentTrack}
                    isPlaying={isPlaying}
                    onSelectTrack={handleSelectTrack}
                    favorites={favorites}
                    onToggleFavorite={handleToggleFavorite}
                  /> */}
                  <MusicLibrary />
                </div>
              </div>
            </div>

            {/* Bottom Military Badge */}
            <div className="flex justify-center mt-6">
              <div className="bg-gradient-to-r from-gold via-gold-light to-lightBeige px-6 py-2 rounded-full shadow-xl border-2 border-gold/30 backdrop-blur-sm">
                <span className="text-primary font-bold text-xs tracking-widest">دولة الإمارات العربية المتحدة 🇦🇪</span>
              </div>
            </div>
          </div>
        </section>

        {/* Program Schedule Section - جدول البرامج */}
        <section 
          id="schedule" 
          className="relative overflow-hidden bg-gradient-to-br from-gold/40 via-white to-gold/40"
        >
          {/* خلفية الخط العربي */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            {/* Calligraphy SVG - Bottom Right */}
            <div className="absolute -bottom-20 -right-20 w-[600px] h-[600px] opacity-15 animate-float">
              <img 
                src={CalligraphySVGBlue}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Calligraphy SVG - Top Left */}
            <div className="absolute -top-20 -left-20 w-[600px] h-[600px] opacity-15 transform rotate-180 animate-float-reverse">
              <img 
                src={CalligraphySVGBlue}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Calligraphy SVG - Center Right (faded more) */}
            <div className="absolute top-1/2 -right-32 transform -translate-y-1/2 w-[500px] h-[500px] opacity-8 animate-float">
              <img 
                src={CalligraphySVGBlue}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Calligraphy SVG - Center Left (faded more) */}
            <div className="absolute top-1/2 -left-32 transform -translate-y-1/2 rotate-180 w-[500px] h-[500px] opacity-8 animate-float-reverse">
              <img 
                src={CalligraphySVGBlue}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          
          <div className="relative z-10">
            <ProgramSchedule />
          </div>
        </section>

        {/* Statistics Section - الإحصائيات */}
        <UpdatedStatistics 
          stats={listeningStats}
          recentlyPlayed={recentlyPlayed}

        />



        {/* News Section - الأخبار */}
        {/* <UpdatedNewsSection /> */}

        {/* Footer - الفوتر */}
        <NewFooter />

        {/* Queue Floating Button & Panel */}
        {/* <Queue
          queue={queue}
          currentTrack={currentTrack}
          onReorder={handleQueueReorder}
          onRemove={handleRemoveFromQueue}
          onClear={handleClearQueue}
          onPlay={handleSelectTrack}
        /> */}

        {/* Advanced Settings Floating Button & Panel */}
        {/* <AdvancedSettings onSleepTimerSet={handleSleepTimer} /> */}
      </main>
    </div>
  );
}

export default App;
